import React, { useContext, useState, useMemo } from 'react';
import { View } from 'react-native';
import { useMutation } from '@apollo/client';
import styled from 'styled-components/native';

import { Text } from 'src/styles';
import * as colors from 'src/styles/colors';
import { dollars } from 'src/utils/stringHelpers';
import AuthContext from 'src/contexts/AuthContext';
import MaxBid from 'src/components/Inventory/MaxBid';

import {
  Title,
  Subtitle,
  Field,
  Value,
  Split,
  Button,
} from './elements';
import { CREATE_BID, SALE } from './queries';

const Bid = styled(Value)`
  background-color: ${props => (props.latest ? colors.elRed : '#fff')};
  color: ${props => (props.latest ? '#fff' : '#000')};
  padding: 12.5px;
  width: ${props => (props.smallScreen ? '140px' : 'fit-content')};
  min-width: ${props => (props.smallScreen ? '140px' : '220px')};
  border-radius: 7.5px;
  text-align: center;
  line-height: 18px;
  border: ${props => (props.latest ? 'none' : '1px solid black')};
`;

const buyerDetails = ({ bid, masked }) => {
  const buyer = bid.buyer;

  if (buyer) {
    let paddleNumber = bid.paddleNumber || '';
    if (masked && paddleNumber.length >= 2) {
      paddleNumber = `${paddleNumber[0]}***${paddleNumber[paddleNumber.length - 1]}`;
    }
    let details = paddleNumber;
    if (buyer.state) details = `${details}${details ? ' - ' : ''}${buyer.state.toUpperCase()}`;
    return details || 'Buyer';
  } else if (bid.paddleService) {
    return bid.paddleService.code;
  } else {
    return 'Floor';
  }
};

const maxBidButtonText = (saleAsset) => {
  const myMax = saleAsset?.myMaxBidPrice;
  const surpassed = !!saleAsset?.myMaxBidSurpassed;

  if (!myMax) return 'Enter Max Bid';
  if (surpassed) return 'Enter New Max Bid';
  return `Max Bid: ${dollars(myMax)}`;
};

const Bids = ({ saleAsset, sale, smallScreen, remainingMs }) => {
  const { currentUser, isLoggedIn } = useContext(AuthContext);
  const [price, setPrice] = useState('');
  const [showPreBid, setShowPreBid] = useState(false);

  const isExpired = typeof remainingMs === 'number' && remainingMs <= 0;

  const maxBidTitle = useMemo(
    () => maxBidButtonText(saleAsset),
    [saleAsset?.myMaxBidPrice, saleAsset?.myMaxBidSurpassed]
  );

  const currentBidAmount = useMemo(() => {
    if (saleAsset?.biddingNextManual != null && saleAsset?.biddingNextManual !== '') {
      return saleAsset.biddingNextManual;
    }
    return saleAsset?.biddingNext;
  }, [saleAsset?.biddingNextManual, saleAsset?.biddingNext]);

  const [createBid] = useMutation(CREATE_BID, {
    onCompleted: () => setPrice(''),
    refetchQueries: sale?.id ? [{ query: SALE, variables: { id: sale.id } }] : [],
    optimisticResponse:
      (saleAsset?.id && (price || currentBidAmount))
        ? {
            __typename: 'Mutation',
            createBid: {
              __typename: 'CreateBidPayload',
              bid: {
                __typename: 'Bid',
                id: `optimistic-${Date.now()}`,
                price: String(price || currentBidAmount),
                note: null,
                paddleNumber: currentUser?.buyer?.paddleNumber || '',
                paddleService: null,
                removedAt: null,
                buyer: currentUser?.buyer
                  ? {
                      __typename: 'Buyer',
                      id: currentUser.buyer.id,
                      city: currentUser.buyer.city || null,
                      state: currentUser.buyer.state || null,
                    }
                  : null,
              },
              errors: [],
            },
          }
        : undefined,
    update: (cache, { data: resp }) => {
      const newBid = resp?.createBid?.bid;
      if (!newBid || !saleAsset?.id) return;

      const saId = cache.identify({ __typename: 'SaleAsset', id: saleAsset.id });
      if (!saId) return;

      cache.modify({
        id: saId,
        fields: {
          bids(existing = []) {
            return [...existing, newBid];
          },
          highBid(existing) {
            return newBid.price != null ? String(newBid.price) : existing;
          },
        },
      });
    },
  });

  if (!saleAsset) return <Text>No Active Bidding</Text>;

  const enterBid = (newPrice) => {
    if (isExpired) return;
    const toSend = newPrice ?? price;
    if (!toSend) return;

    createBid({
      variables: { price: toSend, saleAssetId: saleAsset.id },
    });
  };

  const bids = (saleAsset.bids ?? []).filter(bid => !bid.removedAt).slice().reverse();
  const highBid = bids[0]?.buyer && bids[0].buyer.id === currentUser?.buyer?.id;
  const sameBuyer = bid => currentUser && bid.buyer?.id === currentUser?.buyer?.id;

  return (
    <View>
      {sale.bidGroupChoiceId && (
        <>
          <Title>Buyer Making Selections</Title>
          <Split />
        </>
      )}

      {isLoggedIn && currentUser?.buyer?.isApproved && !sale.bidGroupChoiceId && (
        <>
          <Field>
            <Subtitle style={{ alignSelf: 'center', marginRight: 100 }}>
              {isExpired ? 'Bidding Closed' : (highBid ? 'You Have Current High Bid' : 'Enter Bid')}
            </Subtitle>

            <Button
              disabled={highBid || isExpired || currentBidAmount == null}
              title={dollars(currentBidAmount)}
              onPress={() => enterBid(currentBidAmount)}
            />

            {' '}
            <Button
              disabled={isExpired}
              title={maxBidTitle}
              onPress={() => setShowPreBid(true)}
              style={{ width: '160px', marginLeft: '10px' }}
            />
          </Field>

          {isExpired && (
            <Text style={{ marginTop: 10, fontWeight: '700' }}>
              Time has expired for this lot — bidding is closed.
            </Text>
          )}

          <Field>
            {showPreBid && !isExpired && (
              <MaxBid
                saleAsset={saleAsset}
                setShow={setShowPreBid}
                saleId={sale?.id}
              />
            )}
          </Field>

          <Split />
        </>
      )}

      <Subtitle>Bids</Subtitle>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          gap: '3px',
          overflow: 'scroll',
        }}
      >
        {bids.map(bid => (
          <Bid
            key={bid.id}
            sameBuyer={sameBuyer(bid)}
            latest={bid === bids[0]}
            smallScreen={smallScreen}
          >
            <span style={{ fontWeight: 700 }}>
              {dollars(bid.price)}<br />
            </span>
            <span style={{ fontWeight: 400, display: 'flex', flexDirection: 'column' }}>
              {bid.note && `${bid.note}`}
              {buyerDetails({ bid, masked: !sameBuyer(bid) })}
            </span>
          </Bid>
        ))}
      </div>
    </View>
  );
};

export default Bids;